import { useState } from "react";
import { useParams } from "react-router-dom";

import { Hero } from "../../components/Hero";
import { I } from "../../components/icons";
import { Notice } from "../../components/Notice";
import { NavRow, RowGroup } from "../../components/Rows";
import { Stat, StatStrip } from "../../components/StatStrip";
import { Tag } from "../../components/Tag";
import { dayLabel, dayMonth, parseDay, todayStr, weekDays } from "../../domain/date";
import { familyTraining, pronouns, weekRangeLabel } from "../../domain/family";
import { sessionKeyFor, typeName } from "../../domain/plan";
import { walkDayName } from "../../domain/programme";
import { answeredCount } from "../../domain/trainingQuestions";
import { weekDone, weekRows } from "../../domain/week";
import { useLinked } from "../../lib/linked";
import { useKaya } from "../../store/kayaStore";
import { LinkedShell, familyPath, setByWords, useMyName } from "./linkedPage";

const WEEKDAY = (ds: string) => parseDay(ds).toLocaleDateString("en-GB", { weekday: "long" });

/**
 * You › Family › one person › Training (board FamilyTraining; ADR-056): their week as plain
 * figures — strength days, walk minutes, the last session and its How was it?, the last chair
 * stand beside its range, the retest — with no streak, no red and nothing "behind". The owner can
 * change their exercise answers and ask to be told when a week is missed; starting a session and
 * the safety questions stay on their phone.
 */
export default function FamilyTraining() {
  const { id } = useParams<{ id: string }>();
  const q = useLinked(id);
  const me = useMyName();
  const showToast = useKaya((s) => s.showToast);
  const [busy, setBusy] = useState(false);
  const today = todayStr();

  const notify = async (on: boolean) => {
    setBusy(true);
    try {
      await q.setNotify(on);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not reach Kaya's server");
    } finally {
      setBusy(false);
    }
  };

  return (
    <LinkedShell q={q} title={(name) => (name ? `${name} · training` : "Training")} back={{ to: familyPath(id), label: "Back" }}>
      {({ S, name, synced, notifyMissedWeek, readerDevices }) => {
        const p = pronouns(S);
        const f = familyTraining(S, today);
        const week = weekDays(parseDay(today));
        const range = weekRangeLabel(week[0], week[6]);
        const key = sessionKeyFor(S, today);
        const todayName = key === "R" ? "rest day" : (walkDayName(S, key) ?? typeName(key));
        const rows = weekRows(S, today);
        const todayRow = rows.find((r) => r.ds === today);
        const strengthToday = !!todayRow && (todayRow.key === "SB" || todayRow.key === "CH") && todayRow.state !== "done";
        const kaya = weekDone(rows);
        const by = setByWords(f.setBy, me);
        const count = answeredCount(S, new Date(), { subject: true });
        const answeredLine = f.answeredOn
          ? `${count ? `${count.n} of ${count.of} · ` : ""}${f.setBy ? `${by}, ${dayLabel(f.answeredOn)}` : `answered on ${p.their} phone, ${dayLabel(f.answeredOn)}`}`
          : "Not answered yet";

        return (
          <>
            {!synced ? (
              <Notice tone="info">
                Nothing from {name}’s phone yet. {p.Their} week appears here once Kaya on it has synced.
              </Notice>
            ) : null}

            {f.programme === null ? (
              <Hero
                label="Training"
                title="No exercise answers yet"
                line={`Answer them with ${p.them}, or ask ${p.them} to on ${p.their} phone.`}
              />
            ) : (
              <Hero
                label={
                  <>
                    {f.programme === "steady" ? "Steady" : "Kaya"} · {todayName}
                    {by ? (
                      <>
                        {" "}
                        <Tag tone="neutral">{by}</Tag>
                      </>
                    ) : null}
                  </>
                }
                title={
                  f.strength
                    ? `${f.strength.done} of ${f.strength.of} strength days`
                    : `${kaya.done} of ${kaya.of} sessions`
                }
                line={`this week · ${range}${strengthToday ? ` · ${WEEKDAY(today)}’s is today` : ""}`}
              />
            )}

            <StatStrip ariaLabel={`${name}’s figures`}>
              <Stat
                figure={f.walks?.count ?? null}
                label="Walks"
                note={f.walks ? `${f.walks.min} min · this week` : "this week"}
                to={familyPath(id, "week")}
              />
              <Stat
                figure={f.chairStand?.n ?? null}
                label="Chair stands"
                note={
                  f.chairStand
                    ? f.chairStand.line !== null
                      ? `range starts at ${f.chairStand.line}`
                      : dayLabel(f.chairStand.ds)
                    : "not tested yet"
                }
              />
              <Stat figure={f.retest ? dayMonth(f.retest) : null} label="Retest" note={`on ${p.their} phone`} />
            </StatStrip>

            <RowGroup heading={`${p.Their} training`}>
              <NavRow
                icon={I.calendar}
                title={`${p.Their} week`}
                subtitle={f.weekLine ?? "Nothing due yet this week"}
                to={familyPath(id, "week")}
              />
              <NavRow
                kind="value"
                icon={I.check}
                title="Last session"
                subtitle={
                  f.last
                    ? `${dayLabel(f.last.ds)} · ${f.last.name}${f.last.minutes !== null ? `, ${f.last.minutes} min` : ""}`
                    : "None in the last four weeks"
                }
                value={f.last?.howWas ?? undefined}
              />
              <NavRow
                icon={I.list}
                title="Exercise answers"
                subtitle={answeredLine}
                to={familyPath(id, f.programme === null ? "exercise" : "training-answers")}
              />
            </RowGroup>

            <RowGroup heading="Tell you">
              <NavRow
                kind="toggle"
                icon={I.bell}
                title="Tell me when a week is missed"
                subtitle={
                  notifyMissedWeek && readerDevices === 0
                    ? "Notifications are off on your phones — switch them on under You › Alerts"
                    : `Sunday evening, if no strength day was done: “${name}: 0 of ${f.strength?.of ?? 3} this week”`
                }
                checked={notifyMissedWeek}
                disabled={busy}
                onChange={(on) => void notify(on)}
              />
            </RowGroup>

            <p className="you-fine">
              Starting a session and the safety questions stay on {p.their} phone; Kaya never starts one from yours.
            </p>
          </>
        );
      }}
    </LinkedShell>
  );
}
